import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useDeliverPatient } from '../../../../hooks/useDeliverPatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import { deliverRetainers } from '../../../../services/patient/retention';
import { PatientHeaderCard } from '../PatientApprove/PatientHeaderCard';
import { PatientActionPage } from '../shared/PatientActionPage';
import { RetentionDocsPane } from './RetentionDocsPane';

// Ret-5 "Pending retainers delivery" (GP). Full-page deliver surface modelled
// on PatientDeliver: the two retention documents in the left pane, the patient
// header + delivery instructions on the right, and a footer that fires the
// deliver-retainers transition (Ret-5 -> Done).
const PatientDeliverRetainers: React.FC = () => {
    const navigate = useNavigate();
    const { data: patient, isLoading } = useDeliverPatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [submitting, setSubmitting] = useState(false);

    const patientId = patient?.id ?? null;
    const patientName = useMemo(() => patient?.name ?? '', [patient]);
    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;

    const handleDeliver = async (): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await deliverRetainers(patientId);
            markActionCompleted(patientId, 'Deliver retainers');
            showToast('Retainers marked as delivered', 'success');
            navigate('/home');
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to mark retainers as delivered';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle="Deliver retainers"
            tabIdPrefix="deliver-retainers"
            leftPane={<RetentionDocsPane />}
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        duration={patient?.treatmentDuration ?? null}
                        compact
                    />

                    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                        <p className="text-sm text-gray-600">
                            Congratulations on completing this case! Review the retention documents
                            on the left and sign them with your patient for your internal records.
                            These documents are also accessible at any time in the Resource Center.
                        </p>

                        <div className="bg-blue-50 rounded-xl p-5 flex gap-4">
                            <InformationCircleIcon className="w-6 h-6 text-[#3773A8] flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-gray-800 mb-2">
                                    Retainer delivery day instructions
                                </p>
                                <ul className="space-y-1.5">
                                    <li className="text-sm text-gray-700 flex items-start gap-2">
                                        <span className="mt-1.5 w-1.5 h-1.5 bg-[#3773A8] rounded-full flex-shrink-0" />
                                        Once retainers are delivered, please verify proper fit and
                                        document it. Dental Monitoring will not be resumed.
                                    </li>
                                    <li className="text-sm text-gray-700 flex items-start gap-2">
                                        <span className="mt-1.5 w-1.5 h-1.5 bg-[#3773A8] rounded-full flex-shrink-0" />
                                        The case will be marked as complete once retainers are
                                        marked as delivered.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            }
            footer={
                <div className="flex-shrink-0 mt-3 pt-3 pb-1 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={() => void handleDeliver()}
                        disabled={submitting || !patientId}
                        className={`w-full h-11 rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                            !submitting && patientId
                                ? 'bg-gray-900 text-white hover:bg-gray-800 cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {submitting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                        Mark retainers as delivered →
                    </button>
                </div>
            }
        />
    );
};

export default PatientDeliverRetainers;
